import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { CreateStrategyDialog } from "../Strategies/CreateStrategyDialog";
import type { Report } from "../../../types/reports";
import type { ReportStrategyResponse } from "../../../types/reports";
import type {
  WorkspaceCreateContext,
  WorkspaceDetail,
  WorkspaceEdge,
  WorkspaceNode,
  WorkspaceNodeType,
} from "../../../types/workspace";
import {
  breakWorkspaceNodeLink,
  bulkUpdateWorkspaceNodePositions,
  patchWorkspaceNode,
  registerWorkspaceNode,
  reparentWorkspaceNode,
} from "../../../services/workspacesApi";
import { getBacktest, deleteBacktest } from "../../../services/backtestsApi";
import {
  updateReport,
  getReport,
  createReportStrategyPolicyVersion,
  deleteReport,
  deleteReportStrategy,
  deleteReportStrategyPolicyVersion,
  setReportStrategyPolicyAccounts,
} from "../../../services/reportsApi";
import { deleteFailedGeneratedReport } from "../../../utils/deleteFailedGeneratedReport";
import { toastSimulateWorkspaceSaveFailure, toastWorkspaceApiError } from "../../../utils/workspaceApiErrors";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { WorkspaceCanvas } from "./WorkspaceCanvas";
import { WorkspaceGenerateReportDialog } from "./WorkspaceGenerateReportDialog";
import { WorkspaceSimulatorDialog } from "./WorkspaceSimulatorDialog";
import { WorkspaceRerunSimulationDialog } from "./WorkspaceRerunSimulationDialog";
import type { WorkspaceChildActionKind, WorkspaceChildAddMode } from "./workspaceChildActions";
import { useWorkspaceContext } from "./workspaceContext";
import {
  computeAppendChildNodePosition,
  computeChildNodePosition,
  computeMultiParentMergePosition,
  computeRerunSimulationPosition,
  placeWorkspaceOriginOrphanPositions,
  resolveWorkspaceNodePosition,
  withEnforcedSiblingBranchGaps,
  workspaceGraphLooksUninitialized,
  workspaceGraphHasExcessiveVerticalGaps,
  workspaceNodesHaveOverlap,
  workspaceNodesNeedAutoLayout,
} from "./workspaceLayoutUtils";
import { findWorkspaceNodeByEntity, findWorkspaceNodeForDeepLink, findUpstreamStrategyEntityId, workspaceNodeTitle } from "./workspaceGraphUtils";
import { isUsableTitle } from "./useWorkspaceReportTitles";
import { WORKSPACE_OVERLAY_EXIT_MS } from "./workspaceOverlayMotion";
import {
  findWorkspaceDisconnectTarget,
  listWorkspaceReparentCandidates,
} from "./workspaceReparentUtils";
import { useWorkspaceSimulationSourceReportIds } from "./useWorkspaceSimulationSourceReportIds";
import { useWorkspaceDrawerStack, WorkspaceDrawerStack } from "./WorkspaceDrawerStack";
import { useWorkspaceReportRefresh } from "./useWorkspaceReportRefresh";
import { useWorkspaceAutoLayout } from "./useWorkspaceAutoLayout";
import { computeInsertStrategyNodePosition } from "./workspacePlaceholderUtils";
import { WorkspaceAttachExistingDialog } from "./WorkspaceAttachExistingDialog";
import {
  applyWorkspaceBreakLinkToDetail,
  describeWorkspaceDownstreamRemoval,
  orderWorkspaceNodesForCascadeRemove,
} from "./workspaceCascadeRemove";
import { WorkspaceRemoveNodeDialog } from "./WorkspaceRemoveNodeDialog";
import { workspaceNodeCanDeleteEntity } from "./workspaceNodeDeleteUtils";
import {
  saveSimulateTradePolicyJob,
  SimulateTradePolicyJobStillRunningError,
  waitForSimulateTradePolicyJob,
} from "../../../services/tradePolicyApi";
import {
  hydrateWorkspaceAllSimulationPolicies,
  hydrateWorkspaceReportLineage,
  hydrateWorkspaceSimulationPolicies,
  isWorkspaceSimulationCapReached,
  workspaceSimulationCapReachedCopy,
} from "./workspaceLineageHydrate";
import { ensureStrategyLinkedToAccount } from "./workspaceAttachStrategyAccount";
import { resolveStrategyIdForBacktest } from "../ExecutionRulesSimulator/simulatorPolicyVersionSaved";
import {
  insertOptimisticWorkspaceReportNode,
  insertOptimisticWorkspaceSimulationNode,
  isOptimisticWorkspaceReportNodeId,
  isOptimisticWorkspaceSimulationNodeId,
  isPendingOptimisticWorkspaceNode,
  removeWorkspaceNodeFromDetail,
  shouldSkipWorkspacePositionPersist,
  stampOptimisticReportEntity,
  stampOptimisticSimulationEntity,
  stripOptimisticWorkspaceReportNodes,
  stripOptimisticWorkspaceSimulationNodes,
  workspacePersistNodeId,
  workspacePersistWorkspaceId,
} from "./workspaceOptimisticReport";
import {
  isPersistedWorkspaceResourceId,
  sanitizeWorkspaceSimulateAttachFields,
} from "../../../utils/workspacePersistIds";
import { WorkspaceCombinePoliciesDialog } from "./WorkspaceCombinePoliciesDialog";
import { toastIfMissingAdoptedReport } from "./WorkspaceCombinedSimulationDialog";
import { listCombinablePolicyNodes } from "./workspaceCombinedPolicyUtils";
import { loadWorkspacePolicyRunPrefill, loadWorkspaceSimulationEditPrefill } from "./workspacePolicyRunPrefill";
import { shouldForkDerivedPolicyAfterEditedRun } from "./workspacePolicyForkUtils";
import { useBillingContext } from "../../../contexts/BillingContext";
import { useWorkspaceNodeDeepLink } from "./useWorkspaceNodeDeepLink";
import { useHistory } from "react-router-dom";
import { useActiveBrokerAccount } from "@/contexts/ActiveBrokerAccountContext";
import {
  resolveEntityBrokerAccounts,
  brokerAccountRefKey,
  type BrokerAccountRef,
} from "../../../utils/brokerAccountRef";
import {
  strategyWorkspacePath,
  WORKSPACE_HUB_PATH,
} from "./workspaceRoutes";
import {
  resolveWorkspacePineStrategyEntityId,
  type WorkspaceExecutingAccountDrawerTarget,
} from "./workspaceDrawerEntry";
import {
  executingAccountNodeId,
  listPolicyExecutingAccounts,
} from "./workspaceExecutingAccountUtils";
import { WORKSPACE_LAYOUT_REVISION } from "./workspaceGraphConstants";

function parentNodeIdFor(
  detail: WorkspaceDetail,
  nodeId: string
): string | null {
  return detail.edges.find((edge) => edge.targetNodeId === nodeId)?.sourceNodeId ?? null;
}

export type WorkspaceWizardState = {
  kind: WorkspaceChildActionKind;
  parentNode: WorkspaceNode;
  workspaceContext: WorkspaceCreateContext;
  prefillStrategyId?: string | null;
  /** Strategy node title for generate-report review when prefilled from canvas. */
  prefillStrategyName?: string | null;
  scopedReportId?: string | null;
  /** When set, strategy is inserted on Account → Report and assigned to the report. */
  insertBetweenReportNode?: WorkspaceNode | null;
  /** Run from policy / combined_policy — prefilled simulator. */
  policyRun?: {
    prefillPolicyTree?: unknown;
    prefillPolicy?: import("../../../types/tradePolicy").PolicyTreeV2 | null;
    policyMode?: "v1" | "v2";
    prefillWindow?: {
      start_date?: string | null;
      end_date?: string | null;
      timeframe?: string | null;
    } | null;
    prefillSymbol?: string | null;
    /** Prefer executing-account assignment when set. */
    lockBrokerAccountCode?: string | null;
    candleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
    candleTimeframeLocked?: boolean;
    entryCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
    managementCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
    focusNodeType: "simulation" | "combined_simulation";
    /**
     * Edit in simulator (from a sim): after generate, save a new policy version
     * under the new sim node.
     */
    autoSavePolicyVersion?: boolean;
    /**
     * Edit & run (from policy/CP): when the submitted tree differs from the
     * simulator’s post-hydrate rebuild baseline, create a derived policy under
     * the source and reparent the sim onto it. When unchanged (date-window only):
     * - combined_policy: leave sim under the existing CP
     * - policy: reparent sim under the existing source policy (no derived node)
     */
    saveDerivedPolicyLineage?: boolean;
    /** @deprecated Fork baseline comes from the simulator queue payload only. */
    baselinePolicyTree?: unknown;
    derivedFromPolicyNodeId?: string;
    derivedPolicyNodeType?: "policy" | "combined_policy";
  } | null;
} | null;

export type WorkspaceAttachExistingState = {
  kind: WorkspaceChildActionKind;
  parentNode: WorkspaceNode;
  position: { x: number; y: number };
  insertBetweenReportNode?: WorkspaceNode | null;
  /** When attaching a simulation under a report, only show sims for that report. */
  scopedReportId?: string | null;
} | null;

export function WorkspaceCanvasOverlays(): React.ReactElement {
  const {
    detail,
    loading,
    scope,
    workspaceId,
    viewMode,
    refreshDetail,
    selectedNodeId,
    setSelectedNodeId,
    enterPolicyCombineMode,
    exitPolicyCombineMode,
    setDetail,
    patchNodePositions,
  } = useWorkspaceContext();
  const { simsPerReport } = useBillingContext();
  const isStrategyView = viewMode === "strategy";
  const history = useHistory();
  const { setActiveTradeAccount } = useActiveBrokerAccount();

  const handleOpenStrategyWorkspace = useCallback(
    (node: WorkspaceNode) => {
      if (node.type !== "strategy" || !node.entityId.trim()) return;
      history.push(strategyWorkspacePath(node.entityId));
    },
    [history]
  );

  const [wizard, setWizard] = useState<WorkspaceWizardState>(null);
  const [attachExisting, setAttachExisting] = useState<WorkspaceAttachExistingState>(null);
  const [removeNodeConfirm, setRemoveNodeConfirm] = useState<WorkspaceNode | null>(null);
  const [combineDialog, setCombineDialog] = useState<{
    anchor: WorkspaceNode;
    initialSelectedIds: string[];
  } | null>(null);
  const [rerunSimulationNode, setRerunSimulationNode] = useState<WorkspaceNode | null>(null);
  const [mutating, setMutating] = useState(false);
  /** Submitted policy from the last wizard queue — survives dialog close for lineage. */
  const pendingSubmittedPolicyRef = useRef<unknown>(null);
  const pendingBaselinePolicyRef = useRef<unknown>(null);
  const pendingPolicyChangedFromBaselineRef = useRef<boolean | undefined>(undefined);

  const handleOpenOnAccountCanvas = useCallback(
    (node: WorkspaceNode) => {
      const account =
        resolveEntityBrokerAccounts({
          brokerName: node.brokerName,
          brokerAccountCode: node.brokerAccountCode ?? node.entityId,
        })[0] ?? null;
      if (!account) {
        // Fallback: don't try to guess which canvas; open the hub so the user can
        // pick the workspace among multiples for that account.
        history.push(WORKSPACE_HUB_PATH);
        return;
      }
      void (async () => {
        setMutating(true);
        try {
          // Switch Tessera active account, then open the hub so the user can pick
          // among multiple workspaces for that account.
          await setActiveTradeAccount(account.brokerName, account.brokerAccountCode);
          history.push(WORKSPACE_HUB_PATH);
        } catch (err) {
          toastWorkspaceApiError(err, "Failed to open account workspace");
        } finally {
          setMutating(false);
        }
      })();
    },
    [history, setActiveTradeAccount]
  );

  const handleOpenExecutingAccountWorkspace = useCallback(
    async (account: BrokerAccountRef) => {
      setMutating(true);
      try {
        // Switch Tessera active account, then open the hub so the user can pick
        // among multiple workspaces for that account.
        await setActiveTradeAccount(account.brokerName, account.brokerAccountCode);
        history.push(WORKSPACE_HUB_PATH);
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to open account workspace");
      } finally {
        setMutating(false);
      }
    },
    [history, setActiveTradeAccount]
  );

  const [completionFlashEntityIds, setCompletionFlashEntityIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [dismissingNodeIds, setDismissingNodeIds] = useState<ReadonlySet<string>>(() => new Set());
  const dismissOptimisticTimerRef = useRef<number | null>(null);
  /** Latest detail for dismiss handlers — avoids stale closures vs optimistic inserts. */
  const detailRef = useRef(detail);
  detailRef.current = detail;
  const { drawerEntries, drawerNodes, openDrawer, openExecutingAccountDrawer, closeDrawer, closeAllDrawers, syncFromDetail } =
    useWorkspaceDrawerStack();
  useWorkspaceNodeDeepLink({
    workspaceId,
    detail,
    loading,
    drawerNodes,
    openDrawer,
    setSelectedNodeId,
  });
  const { applyAutoLayout } = useWorkspaceAutoLayout();
  const autoLayoutKeyRef = useRef<string | null>(null);
  const originOrphanKeyRef = useRef<string | null>(null);
  const overlapLayoutKeyRef = useRef<string | null>(null);
  const overlayExitTimerRef = useRef<number | null>(null);
  const flashTimersRef = useRef<Map<string, number>>(new Map());
  const simulationSourceReportIds = useWorkspaceSimulationSourceReportIds(
    detail?.nodes,
    detail?.edges
  );

  /** Register missing POLICY nodes for sims that already have saved policy versions. */
  const policyHydrateKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!detail || !scope || !workspaceId.trim() || loading) return;
    const simKey = detail.nodes
      .filter((n) => n.type === "simulation")
      .map((n) => n.entityId.trim())
      .filter(Boolean)
      .sort()
      .join(",");
    if (!simKey) return;
    const key = `${workspaceId}:${simKey}`;
    if (policyHydrateKeyRef.current === key) return;
    policyHydrateKeyRef.current = key;
    let cancelled = false;
    void (async () => {
      try {
        const added = await hydrateWorkspaceAllSimulationPolicies({
          scope,
          workspaceId,
          detail,
        });
        if (cancelled || added <= 0) return;
        const next = await refreshDetail();
        if (next && next.nodes.length > 1) await applyAutoLayout(next);
      } catch {
        // Soft-fail — canvas still usable without backfilled policy nodes.
        policyHydrateKeyRef.current = null;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyAutoLayout, detail, loading, refreshDetail, scope, workspaceId]);

  const flashReportCompletion = useCallback((reportId: string) => {
    const id = reportId.trim();
    if (!id) return;
    setCompletionFlashEntityIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const existing = flashTimersRef.current.get(id);
    if (existing) window.clearTimeout(existing);
    const timer = window.setTimeout(() => {
      flashTimersRef.current.delete(id);
      setCompletionFlashEntityIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 1800);
    flashTimersRef.current.set(id, timer);
  }, []);

  useWorkspaceReportRefresh(detail, refreshDetail, {
    onReportReady: (reportId, status) => {
      if (String(status).toLowerCase() === "failed") {
        const reportNode = detail
          ? findWorkspaceNodeByEntity(detail, "report", reportId)
          : null;
        if (!reportNode) {
          void deleteFailedGeneratedReport(reportId);
          return;
        }

        setDismissingNodeIds(new Set([reportNode.id]));
        if (selectedNodeId === reportNode.id) {
          setSelectedNodeId(null);
        }

        if (dismissOptimisticTimerRef.current != null) {
          window.clearTimeout(dismissOptimisticTimerRef.current);
        }
        dismissOptimisticTimerRef.current = window.setTimeout(() => {
          dismissOptimisticTimerRef.current = null;
          setDetail((current) =>
            current ? removeWorkspaceNodeFromDetail(current, [reportNode.id]) : current
          );
          setDismissingNodeIds(new Set());
          void (async () => {
            try {
              await breakWorkspaceNodeLink(
                scope,
                workspaceId,
                workspacePersistNodeId(reportNode)
              );
            } catch {
              // Node may already be gone after report delete.
            }
            await deleteFailedGeneratedReport(reportId);
            await refreshDetail();
          })();
        }, 340);
        return;
      }
      flashReportCompletion(reportId);
    },
  });

  useEffect(() => {
    return () => {
      if (overlayExitTimerRef.current != null) {
        window.clearTimeout(overlayExitTimerRef.current);
      }
      if (dismissOptimisticTimerRef.current != null) {
        window.clearTimeout(dismissOptimisticTimerRef.current);
      }
      for (const timer of Array.from(flashTimersRef.current.values())) {
        window.clearTimeout(timer);
      }
      flashTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (detail) syncFromDetail(detail);
  }, [detail, syncFromDetail]);

  const lockBrokerAccountCode = detail?.brokerAccountCode ?? scope.brokerAccountCode;

  const dismissCanvasOverlays = useCallback(() => {
    closeAllDrawers();
    setAttachExisting(null);
  }, [closeAllDrawers]);

  const openWizardAfterOverlayExit = useCallback(
    (nextWizard: NonNullable<WorkspaceWizardState>) => {
      dismissCanvasOverlays();
      setSelectedNodeId(null);
      if (overlayExitTimerRef.current != null) {
        window.clearTimeout(overlayExitTimerRef.current);
      }
      overlayExitTimerRef.current = window.setTimeout(() => {
        overlayExitTimerRef.current = null;
        setWizard(nextWizard);
      }, WORKSPACE_OVERLAY_EXIT_MS);
    },
    [dismissCanvasOverlays, setSelectedNodeId]
  );

  const openWizard = useCallback(
    (
      parentNode: WorkspaceNode,
      kind: WorkspaceChildActionKind,
      mode: WorkspaceChildAddMode = "create"
    ) => {
      if (!detail) return;
      if (isStrategyView) {
        toast.info("Create on an account canvas", {
          description: "Strategy workspace is combine-only. Open a node on its account canvas to add reports or simulations.",
        });
        return;
      }

      if (
        kind === "simulation" &&
        parentNode.type === "report" &&
        isWorkspaceSimulationCapReached(parentNode.id, detail.nodes, detail.edges, simsPerReport)
      ) {
        const copy = workspaceSimulationCapReachedCopy(simsPerReport);
        toast.warning(copy.title, { description: copy.description });
        return;
      }

      const position = computeChildNodePosition(parentNode, detail.edges);
      if (mode === "existing") {
        setAttachExisting({
          kind,
          parentNode,
          position,
          insertBetweenReportNode: null,
          scopedReportId:
            kind === "simulation" &&
            parentNode.type === "report" &&
            isPersistedWorkspaceResourceId(parentNode.entityId)
              ? parentNode.entityId
              : null,
        });
        return;
      }
      const workspaceContext: WorkspaceCreateContext = {
        workspaceId: workspacePersistWorkspaceId(parentNode, workspaceId),
        parentNodeId: workspacePersistNodeId(parentNode),
        position,
      };
      const nextWizard: NonNullable<WorkspaceWizardState> = {
        kind,
        parentNode,
        workspaceContext,
        prefillStrategyId: parentNode.type === "strategy" ? parentNode.entityId : null,
        // Prefer meta title; never pass the generic type label ("Strategy") —
        // useGenerateReportFlow will GET /report-strategies/:id for the real name.
        prefillStrategyName:
          parentNode.type === "strategy" && isUsableTitle(parentNode.meta?.title)
            ? parentNode.meta!.title!.trim()
            : null,
        scopedReportId:
          parentNode.type === "report" && isPersistedWorkspaceResourceId(parentNode.entityId)
            ? parentNode.entityId
            : null,
        insertBetweenReportNode: null,
      };
      // Create-report uses a bottom sheet — clear node sheets / pine cards first.
      if (kind === "report") {
        openWizardAfterOverlayExit(nextWizard);
        return;
      }
      setWizard(nextWizard);
    },
    [detail, isStrategyView, openWizardAfterOverlayExit, simsPerReport, workspaceId]
  );

  const openInsertStrategyBetween = useCallback(
    (
      accountNode: WorkspaceNode,
      reportNode: WorkspaceNode,
      mode: WorkspaceChildAddMode = "create"
    ) => {
      if (!detail) return;
      const position = computeInsertStrategyNodePosition(accountNode, reportNode);
      if (mode === "existing") {
        setAttachExisting({
          kind: "strategy",
          parentNode: accountNode,
          position,
          insertBetweenReportNode: reportNode,
        });
        return;
      }
      setWizard({
        kind: "strategy",
        parentNode: accountNode,
        workspaceContext: {
          workspaceId,
          parentNodeId: workspacePersistNodeId(accountNode),
          position,
        },
        insertBetweenReportNode: reportNode,
        scopedReportId: isPersistedWorkspaceResourceId(reportNode.entityId)
          ? reportNode.entityId
          : null,
      });
    },
    [detail, workspaceId]
  );

  const closeWizard = useCallback(() => {
    if (overlayExitTimerRef.current != null) {
      window.clearTimeout(overlayExitTimerRef.current);
      overlayExitTimerRef.current = null;
    }
    setWizard(null);
  }, []);
  const closeAttachExisting = useCallback(() => setAttachExisting(null), []);

  const persistBranchGapShifts = useCallback(
    (shifts: Array<{ nodeId: string; x: number; y: number }>, nodes: WorkspaceNode[]) => {
      if (shifts.length === 0) return;
      const nodeById = new Map(nodes.map((node) => [node.id, node]));
      const payload = shifts
        .map((shift) => {
          const node = nodeById.get(shift.nodeId);
          if (!node || shouldSkipWorkspacePositionPersist(node)) return null;
          return {
            nodeId: workspacePersistNodeId(node),
            x: shift.x,
            y: shift.y,
          };
        })
        .filter((entry): entry is { nodeId: string; x: number; y: number } => entry != null);

      if (payload.length === 0) return;
      patchNodePositions(payload);
      if (isStrategyView) return;
      void bulkUpdateWorkspaceNodePositions(scope, workspaceId, { positions: payload }).catch(
        (err) => {
          toastWorkspaceApiError(err, "Failed to save layout spacing");
        }
      );
    },
    [isStrategyView, patchNodePositions, scope, workspaceId]
  );

  // On enter: full tidy only for virgin/dumped graphs. Stray {0,0} nodes get a
  // parent-relative park so custom layouts survive leave/return.
  // Strategy federated graphs never persist layout — always tidy strategy-first.
  useEffect(() => {
    if (!detail || detail.nodes.length <= 1) return;
    if (detail.nodes.some((node) => isPendingOptimisticWorkspaceNode(node))) {
      return;
    }

    if (isStrategyView) {
      const key = `${detail.id}:strategy-full:v${WORKSPACE_LAYOUT_REVISION}:${detail.nodes
        .map((node) => node.id)
        .sort()
        .join(",")}`;
      if (autoLayoutKeyRef.current === key) return;
      autoLayoutKeyRef.current = key;
      void applyAutoLayout(detail);
      return;
    }

    if (workspaceGraphLooksUninitialized(detail.nodes)) {
      const key = `${detail.id}:full:v${WORKSPACE_LAYOUT_REVISION}:${detail.nodes.length}:${detail.nodes
        .map((node) => `${node.id}:${Math.round(node.position.x)},${Math.round(node.position.y)}`)
        .join("|")}`;
      if (autoLayoutKeyRef.current === key) return;
      autoLayoutKeyRef.current = key;
      void applyAutoLayout(detail);
      return;
    }

    const orphanShifts = placeWorkspaceOriginOrphanPositions(detail.nodes, detail.edges);
    if (orphanShifts.length === 0) return;
    const orphanKey = `${detail.id}:orphans:${orphanShifts
      .map((shift) => shift.nodeId)
      .sort()
      .join(",")}`;
    if (originOrphanKeyRef.current === orphanKey) return;
    originOrphanKeyRef.current = orphanKey;
    persistBranchGapShifts(orphanShifts, detail.nodes);
  }, [applyAutoLayout, detail, isStrategyView, persistBranchGapShifts]);

  // Overlapping cards or sparse vertical corridors → Organize. Fingerprint
  // includes positions so a successful layout does not loop.
  useEffect(() => {
    if (!detail || detail.nodes.length <= 1) return;
    if (detail.nodes.some((node) => isPendingOptimisticWorkspaceNode(node))) {
      return;
    }
    if (
      !workspaceNodesHaveOverlap(detail.nodes) &&
      !workspaceGraphHasExcessiveVerticalGaps(detail.nodes)
    ) {
      return;
    }

    const key = `${detail.id}:overlap:v${WORKSPACE_LAYOUT_REVISION}:${detail.nodes
      .map(
        (node) =>
          `${node.id}:${Math.round(node.position.x)},${Math.round(node.position.y)}`
      )
      .sort()
      .join("|")}`;
    if (overlapLayoutKeyRef.current === key) return;
    overlapLayoutKeyRef.current = key;
    void applyAutoLayout(detail);
  }, [applyAutoLayout, detail]);

  const focusCreatedNode = useCallback(
    async (
      type: WorkspaceNode["type"],
      entityId: string,
      options?: {
        openDrawer?: boolean;
        /** When set, register the entity under this parent if refresh does not show it yet. */
        ensureParent?: WorkspaceNode | null;
        ensurePosition?: { x: number; y: number } | null;
        ensureMeta?: import("../../../types/workspace").WorkspaceNodeMeta;
      }
    ) => {
      let next = await refreshDetail();
      if (!next) return;

      let node = findWorkspaceNodeForDeepLink(next, type, entityId);
      if (
        !node &&
        options?.ensureParent &&
        (type === "simulation" || type === "combined_simulation")
      ) {
        const parent = options.ensureParent;
        const attachWorkspaceId = workspacePersistWorkspaceId(parent, workspaceId);
        const parentNodeId = workspacePersistNodeId(parent);
        if (
          isPersistedWorkspaceResourceId(attachWorkspaceId) &&
          isPersistedWorkspaceResourceId(parentNodeId) &&
          isPersistedWorkspaceResourceId(entityId)
        ) {
          try {
            await registerWorkspaceNode(scope, attachWorkspaceId, {
              type,
              entityId,
              parentNodeId,
              ...(options.ensurePosition ? { position: options.ensurePosition } : {}),
              ...(options.ensureMeta ? { meta: options.ensureMeta } : {}),
            });
          } catch (err) {
            const slug =
              err && typeof err === "object" && "slug" in err
                ? String((err as { slug?: string }).slug ?? "")
                : "";
            if (slug !== "workspace.duplicate_node") {
              toastWorkspaceApiError(err, "Simulation saved but could not attach to canvas");
            }
          }
          next = (await refreshDetail()) ?? next;
          node = findWorkspaceNodeForDeepLink(next, type, entityId);
        }
      }

      if (node) {
        setSelectedNodeId(node.id);
        if (options?.openDrawer !== false) {
          openDrawer(node);
        }
      }
      // New nodes often land on top of neighbours — reorganize only when needed.
      if (workspaceNodesHaveOverlap(next.nodes) || workspaceNodesNeedAutoLayout(next.nodes)) {
        await applyAutoLayout(next);
      }
    },
    [applyAutoLayout, openDrawer, refreshDetail, scope, setSelectedNodeId, workspaceId]
  );

  const excludeEntityIdsForAttach = useMemo(() => {
    if (!detail || !attachExisting) return new Set<string>();
    const type =
      attachExisting.kind === "simulation" ? "simulation" : attachExisting.kind;
    return new Set(
      detail.nodes
        .filter((node) => node.type === type)
        .map((node) => node.entityId.trim())
        .filter(Boolean)
    );
  }, [attachExisting, detail]);

  const handleAttachExisting = useCallback(
    async (entityId: string) => {
      if (!attachExisting) return;
      const { kind, parentNode, position, insertBetweenReportNode } = attachExisting;
      const nodeType =
        kind === "strategy" ? "strategy" : kind === "report" ? "report" : "simulation";

      setMutating(true);
      try {
        if (nodeType === "strategy") {
          const brokerName = (detail?.brokerName ?? scope.brokerName).trim().toLowerCase();
          const brokerAccountCode = lockBrokerAccountCode.trim();
          if (brokerName && brokerAccountCode) {
            await ensureStrategyLinkedToAccount(entityId, {
              brokerName,
              brokerAccountCode,
            });
          }
        }

        const registered = await registerWorkspaceNode(scope, workspaceId, {
          type: nodeType,
          entityId,
          parentNodeId: workspacePersistNodeId(parentNode),
          position,
        });

        let lineageNote: string | undefined;

        if (insertBetweenReportNode && nodeType === "strategy") {
          await reparentWorkspaceNode(scope, workspaceId, insertBetweenReportNode.id, {
            parentNodeId: registered.node.id,
          });
          await updateReport(insertBetweenReportNode.entityId, { strategy_id: entityId });
          toast.success("Strategy attached", {
            description: "Report is now linked under this strategy.",
          });
        } else if (nodeType === "report") {
          if (parentNode.type === "strategy") {
            await updateReport(entityId, { strategy_id: parentNode.entityId });
          }

          const lineage = await hydrateWorkspaceReportLineage({
            scope,
            workspaceId,
            reportNode: registered.node,
            reportEntityId: entityId,
          });
          const parts: string[] = [];
          if (lineage.simulationsAdded > 0) {
            parts.push(
              `${lineage.simulationsAdded} simulation${lineage.simulationsAdded === 1 ? "" : "s"}`
            );
          }
          if (lineage.policiesAdded > 0) {
            parts.push(
              `${lineage.policiesAdded} polic${lineage.policiesAdded === 1 ? "y" : "ies"}`
            );
          }
          lineageNote =
            parts.length > 0
              ? `Mapped ${parts.join(" and ")} from this report.`
              : undefined;
          toast.success("Report attached to workspace", {
            description: lineageNote,
          });
        } else if (nodeType === "simulation") {
          let strategyIds: string[] = [];
          try {
            const backtest = await getBacktest(entityId, { include_result: false });
            const sid =
              backtest.strategy_id?.trim() ||
              (await resolveStrategyIdForBacktest(backtest)) ||
              "";
            if (sid) strategyIds = [sid];
            if (!sid && backtest.report_id) {
              try {
                const report = await getReport(backtest.report_id);
                const reportSid =
                  typeof report.strategy_id === "string" ? report.strategy_id.trim() : "";
                if (reportSid) strategyIds = [reportSid];
              } catch {
                // ignore
              }
            }
          } catch {
            // Still attach the simulation node; policy hydrate may no-op.
          }

          const policiesAdded = await hydrateWorkspaceSimulationPolicies({
            scope,
            workspaceId,
            simulationNode: registered.node,
            backtestId: entityId,
            strategyIds,
          });
          toast.success("Simulation attached to workspace", {
            description:
              policiesAdded > 0
                ? `Mapped ${policiesAdded} polic${policiesAdded === 1 ? "y" : "ies"} from this simulation.`
                : undefined,
          });
        } else {
          toast.success("Strategy attached to workspace", {
            description: "This account is now linked on the strategy.",
          });
        }

        closeAttachExisting();
        await focusCreatedNode(nodeType, entityId);
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to attach existing entity");
        throw err;
      } finally {
        setMutating(false);
      }
    },
    [attachExisting, closeAttachExisting, detail, focusCreatedNode, lockBrokerAccountCode, scope, workspaceId]
  );

  const handleStrategyCreated = useCallback(
    async (strategy: ReportStrategyResponse) => {
      const insertReportNode = wizard?.insertBetweenReportNode ?? null;
      closeWizard();

      if (insertReportNode) {
        setMutating(true);
        try {
          const next = await refreshDetail();
          const strategyNode = next
            ? findWorkspaceNodeByEntity(next, "strategy", strategy.id)
            : null;
          if (!strategyNode) {
            toast.success("Strategy created");
            await focusCreatedNode("strategy", strategy.id);
            return;
          }

          await reparentWorkspaceNode(scope, workspaceId, insertReportNode.id, {
            parentNodeId: strategyNode.id,
          });
          await updateReport(insertReportNode.entityId, { strategy_id: strategy.id });
          toast.success("Strategy inserted", {
            description: "Report is now linked under this strategy.",
          });
          await focusCreatedNode("strategy", strategy.id);
        } catch (err) {
          toastWorkspaceApiError(err, "Strategy created, but linking the report failed");
          await focusCreatedNode("strategy", strategy.id);
        } finally {
          setMutating(false);
        }
        return;
      }

      toast.success("Strategy added to workspace");
      await focusCreatedNode("strategy", strategy.id);
    },
    [closeWizard, focusCreatedNode, refreshDetail, scope, wizard?.insertBetweenReportNode, workspaceId]
  );

  const handleReportGenerateStarted = useCallback(
    (preview: {
      title: string;
      strategyEntityId?: string | null;
    }): { parentNodeId: string; position: { x: number; y: number } } | void => {
      const parentNode = wizard?.parentNode ?? null;
      const workspaceContext = wizard?.workspaceContext;
      if (!parentNode || !workspaceContext) return;

      let placement: { parentNodeId: string; position: { x: number; y: number } } | undefined;

      setDetail((current) => {
        if (!current) return current;
        const cleaned = stripOptimisticWorkspaceReportNodes(current);
        const strategyEntityId = preview.strategyEntityId?.trim() || null;
        const strategyParent =
          strategyEntityId && parentNode.type === "account"
            ? cleaned.nodes.find(
                (node) => node.type === "strategy" && node.entityId === strategyEntityId
              )
            : null;
        const attachParent = strategyParent ?? parentNode;
        // Live append under lowest sibling — wizard position is frozen at open and
        // can collide, which used to kick full auto-layout and yank edge corridors.
        const position = computeAppendChildNodePosition(
          attachParent,
          cleaned.edges,
          cleaned.nodes
        );
        placement = { parentNodeId: attachParent.id, position };
        const { detail: inserted, nodeId } = insertOptimisticWorkspaceReportNode(cleaned, {
          title: preview.title,
          parentNodeId: attachParent.id,
          position,
        });
        // Growing a strategy's report stack must push lower account branches down
        // so + corridors and branch gaps stay tidy (same gaps as Organize layout).
        const { detail: spaced, shifts } = withEnforcedSiblingBranchGaps(inserted);
        queueMicrotask(() => {
          setSelectedNodeId(nodeId);
          persistBranchGapShifts(shifts, spaced.nodes);
        });
        return spaced;
      });

      return placement;
    },
    [
      persistBranchGapShifts,
      setDetail,
      setSelectedNodeId,
      wizard?.parentNode,
      wizard?.workspaceContext,
    ]
  );

  const handleReportGenerateFailed = useCallback(() => {
    const markDismissing = () => {
      const ids = (detailRef.current?.nodes ?? [])
        .filter((node) => isOptimisticWorkspaceReportNodeId(node.id))
        .map((node) => node.id);
      if (ids.length === 0) return;
      setDismissingNodeIds(new Set(ids));
      if (selectedNodeId && ids.includes(selectedNodeId)) {
        setSelectedNodeId(null);
      }
    };

    // Always strip — generate-started may not have committed into `detail` yet.
    markDismissing();
    requestAnimationFrame(markDismissing);

    if (dismissOptimisticTimerRef.current != null) {
      window.clearTimeout(dismissOptimisticTimerRef.current);
    }
    dismissOptimisticTimerRef.current = window.setTimeout(() => {
      dismissOptimisticTimerRef.current = null;
      setDetail((current) => (current ? stripOptimisticWorkspaceReportNodes(current) : current));
      setDismissingNodeIds(new Set());
    }, 340);
  }, [selectedNodeId, setDetail, setSelectedNodeId]);

  const handleReportGenerated = useCallback(
    async (result: { report: Report; accepted: boolean }) => {
      const parentNode = wizard?.parentNode ?? null;
      const strategyId = result.report.strategy_id?.trim() || null;
      closeWizard();

      // Keep the placeholder visible across the subsequent soft refresh, and stamp it
      // with the real report id so merge can adopt without a blank frame.
      setDetail((current) =>
        current
          ? stampOptimisticReportEntity(current, {
              reportId: result.report.id,
              title: result.report.title ?? undefined,
              status: result.report.status,
            })
          : current
      );

      const generating =
        result.accepted || result.report.status === "processing"
          ? "Generating on canvas — status will update when complete."
          : undefined;

      // Backend should attach Account → Strategy → Report when strategy_id is set.
      // Repair here only if the graph still has the report hanging off the account.
      if (strategyId && parentNode?.type === "account") {
        setMutating(true);
        try {
          let next = await refreshDetail();
          let reportNode = next
            ? findWorkspaceNodeByEntity(next, "report", result.report.id)
            : null;
          let strategyNode = next
            ? findWorkspaceNodeByEntity(next, "strategy", strategyId)
            : null;

          const reportParentId =
            next && reportNode ? parentNodeIdFor(next, reportNode.id) : null;
          const alreadyLinked =
            Boolean(strategyNode) &&
            Boolean(reportNode) &&
            reportParentId === strategyNode?.id;

          if (!alreadyLinked) {
            if (!strategyNode) {
              const position = computeInsertStrategyNodePosition(
                parentNode,
                reportNode ? [reportNode] : []
              );
              try {
                const registered = await registerWorkspaceNode(scope, workspaceId, {
                  type: "strategy",
                  entityId: strategyId,
                  parentNodeId: parentNode.id,
                  position,
                });
                strategyNode = registered.node;
              } catch (err) {
                next = await refreshDetail();
                strategyNode = next
                  ? findWorkspaceNodeByEntity(next, "strategy", strategyId)
                  : null;
                if (!strategyNode) throw err;
              }
            }

            next = await refreshDetail();
            reportNode = next
              ? findWorkspaceNodeByEntity(next, "report", result.report.id)
              : reportNode;
            strategyNode = next
              ? findWorkspaceNodeByEntity(next, "strategy", strategyId) ?? strategyNode
              : strategyNode;

            if (reportNode && strategyNode) {
              const currentParentId = next
                ? parentNodeIdFor(next, reportNode.id)
                : null;
              if (currentParentId !== strategyNode.id) {
                await reparentWorkspaceNode(scope, workspaceId, workspacePersistNodeId(reportNode), {
                  parentNodeId: strategyNode.id,
                });
              }
            }
          }

          toast.success("Report added to workspace", { description: generating });
          await focusCreatedNode("report", result.report.id);
        } catch (err) {
          toastWorkspaceApiError(
            err,
            "Report created, but linking the strategy on the canvas failed"
          );
          await focusCreatedNode("report", result.report.id);
        } finally {
          setMutating(false);
        }
        return;
      }

      toast.success("Report added to workspace", {
        description: generating,
      });
      await focusCreatedNode("report", result.report.id);
    },
    [closeWizard, focusCreatedNode, refreshDetail, scope, setDetail, wizard?.parentNode, workspaceId]
  );

  const handleSimulationFailed = useCallback(() => {
    const markDismissing = () => {
      const ids = (detailRef.current?.nodes ?? [])
        .filter((node) => isOptimisticWorkspaceSimulationNodeId(node.id))
        .map((node) => node.id);
      if (ids.length === 0) return;
      setDismissingNodeIds(new Set(ids));
      if (selectedNodeId && ids.includes(selectedNodeId)) {
        setSelectedNodeId(null);
      }
    };

    markDismissing();
    requestAnimationFrame(markDismissing);

    if (dismissOptimisticTimerRef.current != null) {
      window.clearTimeout(dismissOptimisticTimerRef.current);
    }
    dismissOptimisticTimerRef.current = window.setTimeout(() => {
      dismissOptimisticTimerRef.current = null;
      setDetail((current) =>
        current ? stripOptimisticWorkspaceSimulationNodes(current) : current
      );
      setDismissingNodeIds(new Set());
    }, 340);
  }, [selectedNodeId, setDetail, setSelectedNodeId]);

  const autoSavePolicyVersionAfterEditedRun = useCallback(
    async (args: {
      backtestId: string;
      strategyId: string;
      workspaceId: string;
      focusNodeType: "simulation" | "combined_simulation";
    }) => {
      const { backtestId, strategyId, focusNodeType } = args;
      if (!strategyId.trim() || !backtestId.trim()) return;

      let latest = detailRef.current;
      if (!latest) {
        latest = (await refreshDetail()) ?? null;
      }
      if (!latest) return;

      let simNode =
        findWorkspaceNodeByEntity(latest, focusNodeType, backtestId) ??
        findWorkspaceNodeByEntity(
          latest,
          focusNodeType === "combined_simulation" ? "simulation" : "combined_simulation",
          backtestId
        );
      if (!simNode) {
        latest = (await refreshDetail()) ?? latest;
        simNode =
          findWorkspaceNodeByEntity(latest, focusNodeType, backtestId) ??
          findWorkspaceNodeByEntity(
            latest,
            focusNodeType === "combined_simulation" ? "simulation" : "combined_simulation",
            backtestId
          );
      }
      if (!simNode) return;

      const position = computeAppendChildNodePosition(
        simNode,
        latest.edges,
        latest.nodes
      );
      const attach = sanitizeWorkspaceSimulateAttachFields({
        workspaceId: args.workspaceId,
        parentNodeId: workspacePersistNodeId(simNode),
        position,
      });

      try {
        const created = await createReportStrategyPolicyVersion(strategyId, {
          source_backtest_id: backtestId,
          set_as_current: true,
          ...attach,
        });
        const versionId = String(
          created?.id ??
            created?.policy_version_id ??
            created?.policyVersionId ??
            ""
        ).trim();
        toast.success("Policy version saved", {
          description:
            "A new policy node was added under this simulation so you can find it later.",
        });
        if (versionId) {
          await focusCreatedNode("policy", versionId, {
            openDrawer: false,
            ensureParent: simNode,
            ensurePosition: position,
          });
        } else {
          await refreshDetail();
        }
      } catch (err) {
        toast.warning("Policy version not saved", {
          description:
            "Simulation is on the canvas. Use Save as policy version from results if you want a policy node.",
        });
        toastWorkspaceApiError(err, "Failed to save policy version");
      }
    },
    [focusCreatedNode, refreshDetail]
  );

  const saveDerivedPolicyLineageAfterEditedRun = useCallback(
    async (args: {
      backtestId: string;
      strategyId: string;
      workspaceId: string;
      focusNodeType: "simulation" | "combined_simulation";
      derivedFromPolicyNodeId: string;
      derivedPolicyNodeType: "policy" | "combined_policy";
      baselinePolicyTree?: unknown;
      submittedPolicyTree?: unknown;
      policyChangedFromBaseline?: boolean;
    }) => {
      const {
        backtestId,
        strategyId,
        focusNodeType,
        derivedFromPolicyNodeId,
        derivedPolicyNodeType,
        baselinePolicyTree,
        submittedPolicyTree,
        policyChangedFromBaseline,
      } = args;
      if (
        !strategyId.trim() ||
        !backtestId.trim() ||
        !derivedFromPolicyNodeId.trim() ||
        !args.workspaceId.trim()
      ) {
        return;
      }
      if (!scope) return;

      const shouldFork = shouldForkDerivedPolicyAfterEditedRun({
        baselinePolicyTree,
        submittedPolicyTree,
        policyChangedFromBaseline,
      });

      // Always refresh first — detailRef can still hold the optimistic sim node
      // immediately after focusCreatedNode's setDetail.
      let latest = (await refreshDetail()) ?? detailRef.current;
      if (!latest) return;

      const sourceNode =
        latest.nodes.find((n) => n.id === derivedFromPolicyNodeId) ?? null;
      if (!sourceNode || (sourceNode.type !== "policy" && sourceNode.type !== "combined_policy")) {
        toast.warning("Derived policy not linked", {
          description:
            "Simulation is on the canvas, but the source policy node was not found for lineage.",
        });
        return;
      }

      const findSimNode = (detail: typeof latest) =>
        findWorkspaceNodeByEntity(detail!, focusNodeType, backtestId) ??
        findWorkspaceNodeByEntity(
          detail!,
          focusNodeType === "combined_simulation" ? "simulation" : "combined_simulation",
          backtestId
        );

      let simNode = findSimNode(latest);
      if (simNode && shouldSkipWorkspacePositionPersist(simNode)) {
        latest = (await refreshDetail()) ?? latest;
        simNode = findSimNode(latest);
      }
      if (simNode && shouldSkipWorkspacePositionPersist(simNode)) {
        toast.warning(
          shouldFork
            ? "Simulation not moved under derived policy"
            : "Simulation not moved under source policy",
          {
            description:
              "The simulation node was not persisted yet. Reparent it onto the policy after refresh.",
          }
        );
        simNode = null;
      }

      // Date-window / timeframe-only: no new policy version.
      if (!shouldFork) {
        // Combined sims already register under the CP.
        if (derivedPolicyNodeType === "combined_policy") return;

        // Single policy: sim was attached under the report — hang it off the
        // existing source policy instead of forking a derived node.
        if (!simNode) return;
        const registerWorkspaceId = workspacePersistWorkspaceId(
          sourceNode,
          args.workspaceId
        );
        const parentNodeId = workspacePersistNodeId(sourceNode);
        if (
          !isPersistedWorkspaceResourceId(registerWorkspaceId) ||
          !isPersistedWorkspaceResourceId(parentNodeId)
        ) {
          toast.warning("Simulation not moved under source policy", {
            description: "Could not resolve a persisted workspace parent for the source policy.",
          });
          return;
        }
        const attachPosition = computeAppendChildNodePosition(
          sourceNode,
          latest.edges,
          latest.nodes
        );
        try {
          await reparentWorkspaceNode(scope, registerWorkspaceId, workspacePersistNodeId(simNode), {
            parentNodeId,
          });
          await focusCreatedNode(focusNodeType, backtestId, {
            openDrawer: false,
            ensureParent: sourceNode,
            ensurePosition: attachPosition,
          });
        } catch (reparentErr) {
          toast.warning("Simulation not moved under source policy", {
            description:
              "The simulation is on the canvas under the report. Reparent it onto the policy if needed.",
          });
          toastWorkspaceApiError(reparentErr, "Failed to reparent simulation");
        }
        return;
      }

      try {
        const created = await createReportStrategyPolicyVersion(strategyId, {
          source_backtest_id: backtestId,
          set_as_current: true,
        });
        const versionId = String(
          created?.id ??
            created?.policy_version_id ??
            created?.policyVersionId ??
            ""
        ).trim();
        if (!versionId) {
          toast.warning("Policy version not linked", {
            description:
              "Simulation is on the canvas, but the new policy version id was missing.",
          });
          return;
        }

        const derivedPosition = computeAppendChildNodePosition(
          sourceNode,
          latest.edges,
          latest.nodes
        );
        const registerWorkspaceId = workspacePersistWorkspaceId(
          sourceNode,
          args.workspaceId
        );
        const parentNodeId = workspacePersistNodeId(sourceNode);
        if (
          !isPersistedWorkspaceResourceId(registerWorkspaceId) ||
          !isPersistedWorkspaceResourceId(parentNodeId)
        ) {
          toast.warning("Derived policy not linked", {
            description: "Could not resolve a persisted workspace parent for the source policy.",
          });
          return;
        }

        const meta =
          derivedPolicyNodeType === "combined_policy"
            ? {
                ...(sourceNode.meta?.adoptedReportId
                  ? { adoptedReportId: sourceNode.meta.adoptedReportId }
                  : {}),
                ...(sourceNode.meta?.title ? { title: sourceNode.meta.title } : {}),
                isCombined: true,
                isFork: true,
                forkedFromEntityId: sourceNode.entityId,
              }
            : {
                ...(sourceNode.meta?.title ? { title: sourceNode.meta.title } : {}),
                isFork: true,
                forkedFromEntityId: sourceNode.entityId,
              };

        const registered = await registerWorkspaceNode(scope, registerWorkspaceId, {
          type: derivedPolicyNodeType,
          entityId: versionId,
          parentNodeId,
          position: derivedPosition,
          meta,
        });

        const derivedPersistId = workspacePersistNodeId(registered.node);
        if (!isPersistedWorkspaceResourceId(derivedPersistId)) {
          toast.warning("Simulation not moved under derived policy", {
            description: "Derived policy registered, but its node id was not usable for reparent.",
          });
          await focusCreatedNode(derivedPolicyNodeType, versionId, {
            openDrawer: false,
            ensureParent: sourceNode,
            ensurePosition: derivedPosition,
            ensureMeta: meta,
          });
          return;
        }

        if (!simNode) {
          toast.warning("Simulation not moved under derived policy", {
            description:
              "Could not find the new simulation on the canvas to reparent under the derived policy.",
          });
        } else {
          try {
            await reparentWorkspaceNode(scope, registerWorkspaceId, workspacePersistNodeId(simNode), {
              parentNodeId: derivedPersistId,
            });
          } catch (reparentErr) {
            toast.warning("Simulation not moved under derived policy", {
              description:
                "The new policy is linked under the source. Reparent the simulation onto it if needed.",
            });
            toastWorkspaceApiError(reparentErr, "Failed to reparent simulation");
          }
        }

        toast.success(
          derivedPolicyNodeType === "combined_policy"
            ? "Derived combined policy saved"
            : "Derived policy saved",
          {
            description:
              "Linked under the source policy, with the new simulation under this derived node.",
          }
        );

        await focusCreatedNode(derivedPolicyNodeType, versionId, {
          openDrawer: false,
          ensureParent: sourceNode,
          ensurePosition: derivedPosition,
          ensureMeta: meta,
        });
      } catch (err) {
        toast.warning("Derived policy not saved", {
          description:
            "Simulation is on the canvas. Use Save as policy version from results if you want a policy node.",
        });
        toastWorkspaceApiError(err, "Failed to save derived policy lineage");
      }
    },
    [focusCreatedNode, refreshDetail, scope]
  );

  const handleSimulationQueued = useCallback(
    (preview: {
      title: string;
      jobId?: string;
      policy?: unknown;
      baselinePolicyForLineage?: unknown;
      policyChangedFromBaseline?: boolean;
    }) => {
      const parentNode = wizard?.parentNode ?? null;
      const workspaceContext = wizard?.workspaceContext;
      const focusType = wizard?.policyRun?.focusNodeType ?? "simulation";
      if (!parentNode || !workspaceContext) return;

      const nodeType: "simulation" | "combined_simulation" =
        focusType === "combined_simulation" || parentNode.type === "combined_policy"
          ? "combined_simulation"
          : "simulation";

      // Report sim cap — toast and keep the wizard open (do not insert / close).
      if (
        nodeType === "simulation" &&
        parentNode.type === "report" &&
        detail &&
        isWorkspaceSimulationCapReached(
          parentNode.id,
          detail.nodes,
          detail.edges,
          simsPerReport
        )
      ) {
        const copy = workspaceSimulationCapReachedCopy(simsPerReport);
        toast.warning(copy.title, { description: copy.description });
        return;
      }

      pendingSubmittedPolicyRef.current = preview.policy ?? null;
      // Only the post-hydrate rebuild baseline — never the raw stored prefill
      // (rebuild vs stored always looks like an edit and forks a CP).
      pendingBaselinePolicyRef.current = preview.baselinePolicyForLineage ?? null;
      pendingPolicyChangedFromBaselineRef.current = preview.policyChangedFromBaseline;

      // Live append + branch gaps — never reuse the wizard-open freeze (collides
      // once siblings have moved or the stack grew).
      let attachPosition = workspaceContext.position;

      setDetail((current) => {
        if (!current) return current;
        const cleaned = stripOptimisticWorkspaceSimulationNodes(current);
        const liveParent =
          cleaned.nodes.find((node) => node.id === parentNode.id) ?? parentNode;
        const position = computeAppendChildNodePosition(
          liveParent,
          cleaned.edges,
          cleaned.nodes
        );
        const { detail: inserted, nodeId } = insertOptimisticWorkspaceSimulationNode(cleaned, {
          title: preview.title,
          parentNodeId: liveParent.id,
          position,
          nodeType,
        });
        const { detail: spaced, shifts } = withEnforcedSiblingBranchGaps(inserted);
        const placed = spaced.nodes.find((node) => node.id === nodeId);
        if (placed) {
          attachPosition = { ...resolveWorkspaceNodePosition(placed) };
        } else {
          attachPosition = position;
        }
        queueMicrotask(() => {
          setSelectedNodeId(nodeId);
          persistBranchGapShifts(shifts, spaced.nodes);
        });
        return spaced;
      });

      const jobId = preview.jobId?.trim();
      if (!jobId) {
        // Sync path: onSimulationSaved stamps the placeholder next.
        return;
      }

      // Close wizard immediately — job is queued; finish wait/persist on the canvas.
      const attach = {
        workspaceId: workspaceContext.workspaceId,
        parentNodeId: workspaceContext.parentNodeId,
        position: attachPosition,
        meta: workspaceContext.meta,
      };
      const autoSavePolicyVersion = Boolean(wizard?.policyRun?.autoSavePolicyVersion);
      const saveDerivedPolicyLineage = Boolean(wizard?.policyRun?.saveDerivedPolicyLineage);
      const derivedFromPolicyNodeId =
        wizard?.policyRun?.derivedFromPolicyNodeId?.trim() || "";
      const derivedPolicyNodeType = wizard?.policyRun?.derivedPolicyNodeType;
      const baselinePolicyTree = preview.baselinePolicyForLineage;
      const submittedPolicyTree = preview.policy;
      const policyChangedFromBaseline = preview.policyChangedFromBaseline;
      const strategyId = wizard?.prefillStrategyId?.trim() || "";
      closeWizard();

      void (async () => {
        try {
          await waitForSimulateTradePolicyJob(jobId);
          const saved = await saveSimulateTradePolicyJob(jobId, {
            workspaceId: attach.workspaceId,
            parentNodeId: attach.parentNodeId,
            ...(attach.position ? { position: attach.position } : {}),
            ...(attach.meta ? { meta: attach.meta } : {}),
          });
          if (!saved.ok || !saved.backtestId) {
            handleSimulationFailed();
            if (!saved.ok) {
              toastSimulateWorkspaceSaveFailure(saved);
            }
            return;
          }
          setDetail((current) =>
            current
              ? stampOptimisticSimulationEntity(current, {
                  backtestId: saved.backtestId!,
                  status: "complete",
                })
              : current
          );
          flashReportCompletion(saved.backtestId);
          toast.success(
            focusType === "combined_simulation"
              ? "Combined simulation added to workspace"
              : "Simulation added to workspace"
          );
          await focusCreatedNode(focusType, saved.backtestId, {
            openDrawer: false,
            ensureParent: parentNode,
            ensurePosition: attach.position ?? null,
            ensureMeta: attach.meta,
          });
          if (
            saveDerivedPolicyLineage &&
            strategyId &&
            attach.workspaceId &&
            derivedFromPolicyNodeId &&
            (derivedPolicyNodeType === "policy" || derivedPolicyNodeType === "combined_policy")
          ) {
            await saveDerivedPolicyLineageAfterEditedRun({
              backtestId: saved.backtestId,
              strategyId,
              workspaceId: attach.workspaceId,
              focusNodeType: focusType,
              derivedFromPolicyNodeId,
              derivedPolicyNodeType,
              baselinePolicyTree,
              submittedPolicyTree,
              policyChangedFromBaseline,
            });
          } else if (autoSavePolicyVersion && strategyId && attach.workspaceId) {
            await autoSavePolicyVersionAfterEditedRun({
              backtestId: saved.backtestId,
              strategyId,
              workspaceId: attach.workspaceId,
              focusNodeType: focusType,
            });
          }
        } catch (e) {
          if (e instanceof SimulateTradePolicyJobStillRunningError) {
            toast.info("Simulation still running", {
              description: "Status on the canvas will update when it finishes.",
            });
            return;
          }
          handleSimulationFailed();
          toastWorkspaceApiError(e, "Simulation failed");
        }
      })();
    },
    [
      autoSavePolicyVersionAfterEditedRun,
      closeWizard,
      detail,
      flashReportCompletion,
      focusCreatedNode,
      handleSimulationFailed,
      persistBranchGapShifts,
      saveDerivedPolicyLineageAfterEditedRun,
      setDetail,
      setSelectedNodeId,
      simsPerReport,
      wizard?.parentNode,
      wizard?.policyRun?.autoSavePolicyVersion,
      wizard?.policyRun?.derivedFromPolicyNodeId,
      wizard?.policyRun?.derivedPolicyNodeType,
      wizard?.policyRun?.focusNodeType,
      wizard?.policyRun?.saveDerivedPolicyLineage,
      wizard?.prefillStrategyId,
      wizard?.workspaceContext,
    ]
  );

  const handleSimulationSaved = useCallback(
    async (backtestId: string) => {
      const focusType = wizard?.policyRun?.focusNodeType ?? "simulation";
      const parentNode = wizard?.parentNode ?? null;
      const workspaceContext = wizard?.workspaceContext;
      const autoSavePolicyVersion = Boolean(wizard?.policyRun?.autoSavePolicyVersion);
      const saveDerivedPolicyLineage = Boolean(wizard?.policyRun?.saveDerivedPolicyLineage);
      const derivedFromPolicyNodeId =
        wizard?.policyRun?.derivedFromPolicyNodeId?.trim() || "";
      const derivedPolicyNodeType = wizard?.policyRun?.derivedPolicyNodeType;
      const baselinePolicyTree = pendingBaselinePolicyRef.current;
      const submittedPolicyTree = pendingSubmittedPolicyRef.current;
      const policyChangedFromBaseline = pendingPolicyChangedFromBaselineRef.current;
      const strategyId = wizard?.prefillStrategyId?.trim() || "";
      const attachWorkspaceId = workspaceContext?.workspaceId?.trim() || workspaceId;

      setDetail((current) =>
        current
          ? stampOptimisticSimulationEntity(current, {
              backtestId,
              status: "complete",
            })
          : current
      );
      flashReportCompletion(backtestId);
      closeWizard();
      toast.success(
        focusType === "combined_simulation"
          ? "Combined simulation added to workspace"
          : "Simulation added to workspace"
      );
      await focusCreatedNode(focusType, backtestId, {
        openDrawer: false,
        ensureParent: parentNode,
        ensurePosition: workspaceContext?.position ?? null,
        ensureMeta: workspaceContext?.meta,
      });

      if (
        saveDerivedPolicyLineage &&
        strategyId &&
        attachWorkspaceId &&
        derivedFromPolicyNodeId &&
        (derivedPolicyNodeType === "policy" || derivedPolicyNodeType === "combined_policy")
      ) {
        await saveDerivedPolicyLineageAfterEditedRun({
          backtestId,
          strategyId,
          workspaceId: attachWorkspaceId,
          focusNodeType: focusType,
          derivedFromPolicyNodeId,
          derivedPolicyNodeType,
          baselinePolicyTree,
          submittedPolicyTree: submittedPolicyTree ?? undefined,
          policyChangedFromBaseline,
        });
      } else if (autoSavePolicyVersion && strategyId && attachWorkspaceId) {
        await autoSavePolicyVersionAfterEditedRun({
          backtestId,
          strategyId,
          workspaceId: attachWorkspaceId,
          focusNodeType: focusType,
        });
      }
    },
    [
      autoSavePolicyVersionAfterEditedRun,
      closeWizard,
      flashReportCompletion,
      focusCreatedNode,
      saveDerivedPolicyLineageAfterEditedRun,
      setDetail,
      wizard?.parentNode,
      wizard?.policyRun?.autoSavePolicyVersion,
      wizard?.policyRun?.derivedFromPolicyNodeId,
      wizard?.policyRun?.derivedPolicyNodeType,
      wizard?.policyRun?.focusNodeType,
      wizard?.policyRun?.saveDerivedPolicyLineage,
      wizard?.prefillStrategyId,
      wizard?.workspaceContext,
      workspaceId,
    ]
  );

  const handleRerunSimulationStarted = useCallback(
    (preview: { title: string }) => {
      const parentNode = rerunSimulationNode;
      if (!parentNode) return;

      setDetail((current) => {
        if (!current) return current;
        const cleaned = stripOptimisticWorkspaceSimulationNodes(current);
        const position = computeRerunSimulationPosition(
          parentNode,
          cleaned.edges,
          cleaned.nodes
        );
        const { detail: inserted, nodeId } = insertOptimisticWorkspaceSimulationNode(cleaned, {
          title: preview.title,
          parentNodeId: parentNode.id,
          position,
          nodeType:
            parentNode.type === "combined_simulation" ? "combined_simulation" : "simulation",
          meta: {
            isRerun: true,
            rerunOfEntityId: parentNode.entityId,
          },
        });
        queueMicrotask(() => {
          setSelectedNodeId(nodeId);
        });
        return inserted;
      });
    },
    [rerunSimulationNode, setDetail, setSelectedNodeId]
  );

  const handleRerunSimulationFailed = useCallback(
    (options?: { keepDialogOpen?: boolean }) => {
      const markDismissing = () => {
        const ids = (detailRef.current?.nodes ?? [])
          .filter((node) => isOptimisticWorkspaceSimulationNodeId(node.id))
          .map((node) => node.id);
        if (ids.length === 0) return;
        setDismissingNodeIds(new Set(ids));
        if (selectedNodeId && ids.includes(selectedNodeId)) {
          setSelectedNodeId(null);
        }
      };

      // Always strip — rerun-started may not have committed into `detail` yet.
      // Early-return on empty ids left the loading placeholder stuck after fetch failures.
      markDismissing();
      requestAnimationFrame(markDismissing);

      if (dismissOptimisticTimerRef.current != null) {
        window.clearTimeout(dismissOptimisticTimerRef.current);
      }
      dismissOptimisticTimerRef.current = window.setTimeout(() => {
        dismissOptimisticTimerRef.current = null;
        setDetail((current) =>
          current ? stripOptimisticWorkspaceSimulationNodes(current) : current
        );
        setDismissingNodeIds(new Set());
      }, 340);

      if (!options?.keepDialogOpen) setRerunSimulationNode(null);
    },
    [selectedNodeId, setDetail, setSelectedNodeId]
  );

  const handleRerunSimulationSaved = useCallback(
    async (backtestId: string) => {
      const parentNode = rerunSimulationNode;
      setDetail((current) =>
        current
          ? stampOptimisticSimulationEntity(current, {
              backtestId,
              status: "complete",
            })
          : current
      );
      flashReportCompletion(backtestId);
      setRerunSimulationNode(null);
      const focusType =
        parentNode?.type === "combined_simulation" ? "combined_simulation" : "simulation";
      await focusCreatedNode(focusType, backtestId, {
        openDrawer: false,
        ensureParent: parentNode,
        ensureMeta: parentNode
          ? { isRerun: true, rerunOfEntityId: parentNode.entityId }
          : undefined,
      });
    },
    [flashReportCompletion, focusCreatedNode, rerunSimulationNode, setDetail]
  );

  const handleCombinePolicies = useCallback(
    (node: WorkspaceNode) => {
      if (!detail) return;
      if (node.type !== "policy" && node.type !== "combined_policy") return;
      const combinable = listCombinablePolicyNodes(node, detail.nodes, detail.edges);
      if (combinable.length < 2) {
        toast.warning("Need another policy", {
          description:
            "Combine requires another single or combined policy under the same strategy.",
        });
        return;
      }
      closeAllDrawers();
      enterPolicyCombineMode(node.id);
    },
    [closeAllDrawers, detail, enterPolicyCombineMode]
  );

  const handleCombineSelectedPolicies = useCallback(
    (policyNodeIds: string[]) => {
      if (!detail || policyNodeIds.length < 2) return;
      const anchor = detail.nodes.find((n) => n.id === policyNodeIds[0]);
      if (!anchor || (anchor.type !== "policy" && anchor.type !== "combined_policy")) return;
      const combinable = listCombinablePolicyNodes(anchor, detail.nodes, detail.edges);
      if (combinable.length < 2) {
        toast.warning("Need another policy", {
          description:
            "Combine requires another single or combined policy under the same strategy.",
        });
        return;
      }
      setCombineDialog({ anchor, initialSelectedIds: policyNodeIds });
    },
    [detail]
  );

  const handleCombinedPolicyCreated = useCallback(
    async (result: {
      policyVersionId: string;
      sourcePolicyNodeIds: string[];
      adoptedReportId: string | null;
      title?: string | null;
      resolvedCandleTimeframe?: string | null;
      workspaceNode?: WorkspaceNode;
      edges?: WorkspaceEdge[];
      registrationError?: string;
      homeWorkspaceId?: string | null;
    }): Promise<boolean> => {
      if (isStrategyView) {
        if (result.registrationError) {
          toast.error("Combined policy saved but canvas registration failed", {
            description: result.registrationError,
          });
        } else {
          toast.success("Combined policy saved");
        }
        setCombineDialog(null);
        exitPolicyCombineMode();
        await refreshDetail();
        return true;
      }

      let registered = Boolean(result.workspaceNode);
      if (!registered && result.sourcePolicyNodeIds.length >= 2) {
        try {
          const parentNodes = result.sourcePolicyNodeIds
            .map((id) => detail?.nodes.find((n) => n.id === id))
            .filter((n): n is WorkspaceNode => Boolean(n));
          const parentNodeIds = parentNodes.map((n) => workspacePersistNodeId(n));
          const position =
            parentNodes.length > 0
              ? computeMultiParentMergePosition(parentNodes)
              : { x: 0, y: 0 };
          await registerWorkspaceNode(scope, workspaceId, {
            type: "combined_policy",
            entityId: result.policyVersionId,
            parentNodeId: parentNodeIds[0],
            parentNodeIds,
            position,
            meta: {
              title: result.title?.trim() || "Combined policy",
              ...(result.adoptedReportId
                ? { adoptedReportId: result.adoptedReportId }
                : {}),
              ...(result.resolvedCandleTimeframe?.trim()
                ? {
                    candleTimeframe: result.resolvedCandleTimeframe.trim(),
                    resolvedCandleTimeframe: result.resolvedCandleTimeframe.trim(),
                  }
                : {}),
              sourcePolicyVersionIds: parentNodes
                .map((n) => n.entityId)
                .filter((id): id is string => Boolean(id)),
            },
          });
          registered = true;
        } catch (err) {
          const slug =
            err && typeof err === "object" && "slug" in err
              ? String((err as { slug?: string }).slug ?? "")
              : "";
          if (slug === "workspace.duplicate_node") {
            registered = true;
          } else {
            toastWorkspaceApiError(
              err,
              result.registrationError ||
                "Combined policy saved but canvas registration failed"
            );
          }
        }
      } else if (!registered && result.registrationError) {
        toast.error("Combined policy saved but canvas registration failed", {
          description:
            `${result.registrationError} Keep this dialog open and try again, or check the strategy’s policy versions.`,
        });
      }

      if (!registered) {
        return false;
      }

      setCombineDialog(null);
      exitPolicyCombineMode();
      toast.success("Combined policy added to workspace");
      await focusCreatedNode("combined_policy", result.policyVersionId);
      return true;
    },
    [detail, exitPolicyCombineMode, focusCreatedNode, isStrategyView, refreshDetail, scope, workspaceId]
  );

  const handleRunFromPolicy = useCallback(
    async (node: WorkspaceNode) => {
      if (!detail) return;
      if (node.type !== "policy" && node.type !== "combined_policy") return;
      if (isStrategyView) {
        toast.info("Run on an account canvas", {
          description:
            "Open this policy on its account workspace to run a simulation with the policy loaded.",
        });
        return;
      }
      if (node.type === "combined_policy" && !toastIfMissingAdoptedReport(node)) {
        return;
      }

      setMutating(true);
      try {
        const prefill = await loadWorkspacePolicyRunPrefill(
          node,
          detail.nodes,
          detail.edges
        );
        if (
          prefill.focusNodeType === "simulation" &&
          isWorkspaceSimulationCapReached(
            prefill.attachParent.id,
            detail.nodes,
            detail.edges,
            simsPerReport
          )
        ) {
          const copy = workspaceSimulationCapReachedCopy(simsPerReport);
          toast.warning(copy.title, { description: copy.description });
          return;
        }

        const position = computeAppendChildNodePosition(
          prefill.attachParent,
          detail.edges,
          detail.nodes
        );

        // Defer open so the menu click that launched Edit & run cannot fall
        // through onto Continue / Generate in the new dialog.
        window.setTimeout(() => {
          setWizard({
            kind: "simulation",
            parentNode: prefill.attachParent,
            workspaceContext: {
              workspaceId: workspacePersistWorkspaceId(prefill.attachParent, workspaceId),
              parentNodeId: workspacePersistNodeId(prefill.attachParent),
              position,
            },
            prefillStrategyId: prefill.strategyId,
            scopedReportId: isPersistedWorkspaceResourceId(prefill.scopedReportId)
              ? prefill.scopedReportId
              : null,
            policyRun: {
              prefillPolicyTree: prefill.prefillPolicyTree,
              prefillPolicy: prefill.prefillPolicy,
              policyMode: prefill.policyMode,
              prefillWindow: prefill.prefillWindow,
              prefillSymbol: prefill.prefillSymbol,
              candleTimeframe: prefill.candleTimeframe,
              candleTimeframeLocked: prefill.candleTimeframeLocked,
              entryCandleTimeframe: prefill.entryCandleTimeframe,
              managementCandleTimeframe: prefill.managementCandleTimeframe,
              focusNodeType: prefill.focusNodeType,
              saveDerivedPolicyLineage: true,
              // Rebuild baseline comes from the simulator after hydrate — do not
              // seed with the raw stored tree (that always looks like an edit).
              derivedFromPolicyNodeId: prefill.sourcePolicyNode?.id ?? node.id,
              derivedPolicyNodeType:
                node.type === "combined_policy" ? "combined_policy" : "policy",
            },
          });
        }, 300);
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to prepare simulation run");
      } finally {
        setMutating(false);
      }
    },
    [detail, isStrategyView, simsPerReport, workspaceId]
  );

  const handleEditSimulationInSimulator = useCallback(
    async (node: WorkspaceNode) => {
      if (!detail) return;
      if (node.type !== "simulation" && node.type !== "combined_simulation") return;
      if (isStrategyView) {
        toast.info("Edit on an account canvas", {
          description:
            "Open this simulation on its account workspace to load the policy into the simulator.",
        });
        return;
      }

      setMutating(true);
      try {
        const prefill = await loadWorkspaceSimulationEditPrefill(
          node,
          detail.nodes,
          detail.edges
        );
        if (
          prefill.focusNodeType === "simulation" &&
          isWorkspaceSimulationCapReached(
            prefill.attachParent.id,
            detail.nodes,
            detail.edges,
            simsPerReport
          )
        ) {
          const copy = workspaceSimulationCapReachedCopy(simsPerReport);
          toast.warning(copy.title, { description: copy.description });
          return;
        }

        const position = computeAppendChildNodePosition(
          prefill.attachParent,
          detail.edges,
          detail.nodes
        );

        setWizard({
          kind: "simulation",
          parentNode: prefill.attachParent,
          workspaceContext: {
            workspaceId: workspacePersistWorkspaceId(prefill.attachParent, workspaceId),
            parentNodeId: workspacePersistNodeId(prefill.attachParent),
            position,
          },
          prefillStrategyId: prefill.strategyId,
          scopedReportId: isPersistedWorkspaceResourceId(prefill.scopedReportId)
            ? prefill.scopedReportId
            : null,
          policyRun: {
            prefillPolicyTree: prefill.prefillPolicyTree,
            prefillPolicy: prefill.prefillPolicy,
            policyMode: prefill.policyMode,
            prefillWindow: prefill.prefillWindow,
            prefillSymbol: prefill.prefillSymbol,
            candleTimeframe: prefill.candleTimeframe,
            candleTimeframeLocked: prefill.candleTimeframeLocked,
            entryCandleTimeframe: prefill.entryCandleTimeframe,
            managementCandleTimeframe: prefill.managementCandleTimeframe,
            focusNodeType: prefill.focusNodeType,
            autoSavePolicyVersion: true,
          },
        });
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to load simulation policy");
      } finally {
        setMutating(false);
      }
    },
    [detail, isStrategyView, simsPerReport, workspaceId]
  );

  const handleRerunSimulation = useCallback((node: WorkspaceNode) => {
    if (node.type !== "simulation" && node.type !== "combined_simulation") return;
    setRerunSimulationNode(node);
  }, []);

  const handleOpenDetail = useCallback(
    (node: WorkspaceNode) => {
      setSelectedNodeId(node.id);
      openDrawer(node);
    },
    [openDrawer, setSelectedNodeId]
  );

  const handleOpenExecutingAccountDetail = useCallback(
    (target: WorkspaceExecutingAccountDrawerTarget) => {
      openExecutingAccountDrawer(target);
    },
    [openExecutingAccountDrawer]
  );

  const handleRemoveExecutingAccount = useCallback(
    async (parentPolicyNode: WorkspaceNode, account: BrokerAccountRef) => {
      if (!detail) return;

      const strategyId = findUpstreamStrategyEntityId(
        parentPolicyNode.id,
        detail.nodes,
        detail.edges
      );
      if (!strategyId) {
        toast.error("Could not find parent strategy for this policy.");
        return;
      }

      const policyVersionId = parentPolicyNode.entityId?.trim();
      if (!policyVersionId) {
        toast.error("Could not resolve policy version.");
        return;
      }

      const execNodeId = executingAccountNodeId(parentPolicyNode.id, account);
      const current = listPolicyExecutingAccounts(parentPolicyNode);
      const targetKey = brokerAccountRefKey(account);
      const nextAccounts = current.filter((ref) => brokerAccountRefKey(ref) !== targetKey);

      setMutating(true);
      try {
        await setReportStrategyPolicyAccounts(strategyId, policyVersionId, {
          accounts: nextAccounts.map((entry) => ({
            brokerName: entry.brokerName.trim().toLowerCase(),
            brokerAccountCode: entry.brokerAccountCode.trim(),
          })),
        });
        closeDrawer(execNodeId);
        if (selectedNodeId === execNodeId) {
          setSelectedNodeId(null);
        }
        await refreshDetail();
        toast.success("Executing account removed");
      } catch (err) {
        presentMutationError(err, "Could not remove executing account");
        await refreshDetail();
      } finally {
        setMutating(false);
      }
    },
    [closeDrawer, detail, refreshDetail, selectedNodeId, setSelectedNodeId]
  );

  const handleToggleDisabled = useCallback(
    async (node: WorkspaceNode) => {
      setMutating(true);
      try {
        await patchWorkspaceNode(scope, workspaceId, workspacePersistNodeId(node), {
          isDisabled: !node.isDisabled,
        });
        await refreshDetail();
        toast.success(node.isDisabled ? "Node enabled on canvas" : "Node disabled on canvas");
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to update node");
      } finally {
        setMutating(false);
      }
    },
    [refreshDetail, scope, workspaceId]
  );

  const handleBreakLink = useCallback((node: WorkspaceNode) => {
    setRemoveNodeConfirm(node);
  }, []);

  const handleConfirmBreakLink = useCallback(async () => {
    const node = removeNodeConfirm;
    if (!node || !detail) return;

    const cascade = orderWorkspaceNodesForCascadeRemove(node.id, detail.nodes, detail.edges);
    const removeIds = cascade.map((entry) => entry.id);

    setMutating(true);
    try {
      // Single break-link: BE promotes children / keeps multi-parent combined_policy
      // when ≥2 parents remain. Do not FE-cascade-delete shared combined nodes.
      await breakWorkspaceNodeLink(scope, workspaceId, workspacePersistNodeId(node));
      for (const entry of cascade) {
        closeDrawer(entry.id);
      }
      if (selectedNodeId && removeIds.includes(selectedNodeId)) {
        setSelectedNodeId(null);
      }
      setRemoveNodeConfirm(null);
      setDetail((current) =>
        current ? applyWorkspaceBreakLinkToDetail(current, node.id) : current
      );
      const next = await refreshDetail();
      if (next && next.nodes.length > 1) await applyAutoLayout(next);
      toast.success(
        cascade.length > 1
          ? `Removed ${cascade.length} items from workspace`
          : "Removed from workspace"
      );
    } catch (err) {
      toastWorkspaceApiError(err, "Failed to remove from workspace");
      await refreshDetail();
    } finally {
      setMutating(false);
    }
  }, [
    applyAutoLayout,
    closeDrawer,
    detail,
    refreshDetail,
    removeNodeConfirm,
    scope,
    selectedNodeId,
    setDetail,
    setSelectedNodeId,
    workspaceId,
  ]);

  const handleConfirmDeleteEntity = useCallback(async () => {
    const node = removeNodeConfirm;
    if (!node || !detail) return;
    if (!workspaceNodeCanDeleteEntity(node)) {
      toast.warning("Delete not available", {
        description: "This item can only be removed from the canvas.",
      });
      return;
    }

    const entityId = node.entityId.trim();
    const cascade = orderWorkspaceNodesForCascadeRemove(node.id, detail.nodes, detail.edges);
    const removeIds = cascade.map((entry) => entry.id);

    setMutating(true);
    try {
      switch (node.type) {
        case "report":
          await deleteReport(entityId);
          break;
        case "simulation":
        case "combined_simulation":
          await deleteBacktest(entityId);
          break;
        case "strategy":
          await deleteReportStrategy(entityId);
          break;
        case "policy":
        case "combined_policy": {
          const strategyId = findUpstreamStrategyEntityId(node.id, detail.nodes, detail.edges);
          if (!strategyId) {
            toast.warning("Delete failed", {
              description: "Could not resolve the strategy for this policy version.",
            });
            return;
          }
          await deleteReportStrategyPolicyVersion(strategyId, entityId);
          break;
        }
        default:
          toast.warning("Delete not available", {
            description: "This node type cannot be deleted from the canvas.",
          });
          return;
      }

      for (const entry of cascade) {
        closeDrawer(entry.id);
      }
      if (selectedNodeId && removeIds.includes(selectedNodeId)) {
        setSelectedNodeId(null);
      }
      setRemoveNodeConfirm(null);
      setDetail((current) =>
        current ? applyWorkspaceBreakLinkToDetail(current, node.id) : current
      );
      const next = await refreshDetail();
      if (next && next.nodes.length > 1) await applyAutoLayout(next);
      toast.success(
        node.type === "strategy"
          ? "Strategy deleted"
          : node.type === "report"
            ? "Report deleted"
            : node.type === "policy" || node.type === "combined_policy"
              ? "Policy version deleted"
              : "Simulation deleted"
      );
    } catch (err) {
      toastWorkspaceApiError(err, "Failed to delete");
      await refreshDetail();
    } finally {
      setMutating(false);
    }
  }, [
    applyAutoLayout,
    closeDrawer,
    detail,
    refreshDetail,
    removeNodeConfirm,
    selectedNodeId,
    setDetail,
    setSelectedNodeId,
  ]);

  const removeNodeConfirmTitle = removeNodeConfirm
    ? workspaceNodeTitle(removeNodeConfirm, { accountLabel: detail?.brokerAccountCode })
    : "";

  const removeDownstreamNote = useMemo(() => {
    if (!removeNodeConfirm || !detail) return null;
    const cascade = orderWorkspaceNodesForCascadeRemove(
      removeNodeConfirm.id,
      detail.nodes,
      detail.edges
    );
    const descendants = cascade.filter((entry) => entry.id !== removeNodeConfirm.id);
    return describeWorkspaceDownstreamRemoval(removeNodeConfirm, descendants, {
      nodes: detail.nodes,
      edges: detail.edges,
    });
  }, [detail, removeNodeConfirm]);

  const removeCanDeleteEntity = removeNodeConfirm
    ? workspaceNodeCanDeleteEntity(removeNodeConfirm)
    : false;

  const handleReparent = useCallback(
    async (node: WorkspaceNode, newParent: WorkspaceNode) => {
      if (!detail) return;

      // Defense in depth: never attach a simulation under a report it was not
      // run against — sim results are only valid for that report's trade data.
      const allowed = listWorkspaceReparentCandidates(node, detail.nodes, detail.edges, {
        simulationSourceReportIdByEntityId: simulationSourceReportIds,
      });
      if (!allowed.some((candidate) => candidate.id === newParent.id)) {
        toast.error(
          node.type === "simulation" && newParent.type === "report"
            ? "This simulation only belongs under the report it was run from"
            : "That parent is not allowed for this node"
        );
        return;
      }

      setMutating(true);
      try {
        await reparentWorkspaceNode(scope, workspaceId, workspacePersistNodeId(node), {
          parentNodeId: workspacePersistNodeId(newParent),
        });

        // Keep report↔strategy entity link aligned with the canvas edge.
        if (node.type === "report") {
          if (newParent.type === "strategy") {
            await updateReport(node.entityId, { strategy_id: newParent.entityId });
          } else if (newParent.type === "account") {
            await updateReport(node.entityId, { strategy_id: null });
          }
        }

        const next = await refreshDetail();
        if (next && next.nodes.length > 1) await applyAutoLayout(next);
        const parentLabel = workspaceNodeTitle(newParent, {
          accountLabel: detail.brokerAccountCode,
        });
        toast.success(`Moved under ${parentLabel}`);
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to rewire node");
      } finally {
        setMutating(false);
      }
    },
    [
      applyAutoLayout,
      detail,
      refreshDetail,
      scope,
      simulationSourceReportIds,
      workspaceId,
    ]
  );

  const handleDisconnect = useCallback(
    async (node: WorkspaceNode) => {
      if (!detail) return;
      const account = findWorkspaceDisconnectTarget(node, detail.nodes, detail.edges);
      if (!account) {
        toast.error("This node is already disconnected from a mid-chain parent");
        return;
      }

      setMutating(true);
      try {
        await reparentWorkspaceNode(scope, workspaceId, workspacePersistNodeId(node), {
          parentNodeId: account.id,
        });
        if (node.type === "report") {
          await updateReport(node.entityId, { strategy_id: null });
        }
        const next = await refreshDetail();
        if (next && next.nodes.length > 1) await applyAutoLayout(next);
        toast.success("Disconnected — use Move under… to attach it elsewhere");
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to disconnect node");
      } finally {
        setMutating(false);
      }
    },
    [applyAutoLayout, detail, refreshDetail, scope, workspaceId]
  );

  const strategyDialogOpen = wizard?.kind === "strategy";
  const reportDialogOpen = wizard?.kind === "report";
  const simulationDialogOpen = wizard?.kind === "simulation";

  const strategyContext = wizard?.kind === "strategy" ? wizard.workspaceContext : undefined;
  const reportContext = wizard?.kind === "report" ? wizard.workspaceContext : undefined;
  const simulationContext = wizard?.kind === "simulation" ? wizard.workspaceContext : undefined;
  const workspaceSimulationBlockedReason = useMemo(() => {
    if (!detail || !wizard || wizard.kind !== "simulation") return null;
    const parent = wizard.parentNode;
    const focusType = wizard.policyRun?.focusNodeType ?? "simulation";
    if (focusType === "combined_simulation" || parent.type === "combined_policy") {
      return null;
    }
    if (parent.type !== "report") return null;
    if (
      !isWorkspaceSimulationCapReached(parent.id, detail.nodes, detail.edges, simsPerReport)
    ) {
      return null;
    }
    return workspaceSimulationCapReachedCopy(simsPerReport).description;
  }, [detail, simsPerReport, wizard]);

  const pineStrategyEntityId = useMemo(
    () => resolveWorkspacePineStrategyEntityId(drawerEntries, detail, selectedNodeId),
    [detail, drawerEntries, selectedNodeId]
  );

  return (
    <>
      <WorkspaceCanvas
        pineStrategyEntityId={pineStrategyEntityId}
        onAddChild={openWizard}
        onCombinePolicies={handleCombinePolicies}
        onCombineSelectedPolicies={handleCombineSelectedPolicies}
        onRunCombinedSimulation={isStrategyView ? undefined : handleRunFromPolicy}
        onEditSimulationInSimulator={
          isStrategyView ? undefined : handleEditSimulationInSimulator
        }
        onRerunSimulation={isStrategyView ? undefined : handleRerunSimulation}
        onInsertStrategyBetween={isStrategyView ? undefined : openInsertStrategyBetween}
        onAddReportBranch={
          isStrategyView ? undefined : (accountNode, mode) => openWizard(accountNode, "report", mode)
        }
        onOpenDetail={handleOpenDetail}
        onOpenExecutingAccountDetail={handleOpenExecutingAccountDetail}
        onRemoveExecutingAccount={(parentPolicyNode, account) =>
          void handleRemoveExecutingAccount(parentPolicyNode, account)
        }
        onAddExecutingAccount={handleOpenDetail}
        onOpenStrategyWorkspace={handleOpenStrategyWorkspace}
        onOpenOnAccountCanvas={handleOpenOnAccountCanvas}
        onOpenExecutingAccountWorkspace={handleOpenExecutingAccountWorkspace}
        mutating={mutating}
        onToggleDisabled={
          isStrategyView ? undefined : (node) => void handleToggleDisabled(node)
        }
        onBreakLink={isStrategyView ? undefined : (node) => void handleBreakLink(node)}
        onDisconnect={isStrategyView ? undefined : (node) => void handleDisconnect(node)}
        onReparent={
          isStrategyView ? undefined : (node, newParent) => void handleReparent(node, newParent)
        }
        completionFlashEntityIds={completionFlashEntityIds}
        dismissingNodeIds={dismissingNodeIds}
        onDismissOverlays={dismissCanvasOverlays}
        drawerSlot={
          <WorkspaceDrawerStack
            drawerEntries={drawerEntries}
            accountLabel={detail?.brokerAccountCode}
            brokerAccountCode={lockBrokerAccountCode}
            brokerName={detail?.brokerName ?? scope.brokerName}
            workspaceId={workspaceId}
            nodes={detail?.nodes ?? []}
            edges={detail?.edges ?? []}
            onClose={closeDrawer}
            onRefreshWorkspace={async () => {
              const next = await refreshDetail();
              if (next && next.nodes.length > 1) {
                await applyAutoLayout(next);
              }
            }}
          />
        }
      />

      {combineDialog ? (
        <WorkspaceCombinePoliciesDialog
          open
          onOpenChange={(open) => {
            if (!open) setCombineDialog(null);
          }}
          workspaceId={workspaceId}
          viewMode={viewMode}
          nodes={detail?.nodes ?? []}
          edges={detail?.edges ?? []}
          anchorPolicyNode={combineDialog.anchor}
          initialSelectedIds={combineDialog.initialSelectedIds}
          onCombined={handleCombinedPolicyCreated}
        />
      ) : null}

      {rerunSimulationNode ? (
        <WorkspaceRerunSimulationDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              // Safety net: clear any leftover loading placeholder if dismiss raced.
              setDetail((current) =>
                current ? stripOptimisticWorkspaceSimulationNodes(current) : current
              );
              setDismissingNodeIds(new Set());
              setRerunSimulationNode(null);
            }
          }}
          scope={scope}
          workspaceId={workspaceId}
          sourceSimulationNode={rerunSimulationNode}
          detail={detail}
          lockBrokerAccountCode={lockBrokerAccountCode}
          onRerunStarted={handleRerunSimulationStarted}
          onRerunFailed={handleRerunSimulationFailed}
          onSimulationSaved={(backtestId) => {
            void handleRerunSimulationSaved(backtestId);
          }}
        />
      ) : null}
      {attachExisting ? (
        <WorkspaceAttachExistingDialog
          open
          onOpenChange={(open) => {
            if (!open) closeAttachExisting();
          }}
          kind={attachExisting.kind}
          brokerAccountCode={lockBrokerAccountCode}
          scopedReportId={attachExisting.scopedReportId}
          excludeEntityIds={excludeEntityIdsForAttach}
          onAttach={handleAttachExisting}
        />
      ) : null}

      <WorkspaceRemoveNodeDialog
        open={removeNodeConfirm != null}
        onOpenChange={(open) => {
          if (!open && !mutating) setRemoveNodeConfirm(null);
        }}
        node={removeNodeConfirm}
        nodeTitle={removeNodeConfirmTitle}
        downstreamNote={removeDownstreamNote}
        canDeleteEntity={removeCanDeleteEntity}
        busy={mutating && removeNodeConfirm != null}
        onConfirmRemove={() => void handleConfirmBreakLink()}
        onConfirmDelete={() => void handleConfirmDeleteEntity()}
      />

      {strategyContext ? (
        <CreateStrategyDialog
          open={strategyDialogOpen}
          onOpenChange={(open) => {
            if (!open) closeWizard();
          }}
          workspaceContext={strategyContext}
          brokerAccountCode={lockBrokerAccountCode}
          brokerName={scope.brokerName}
          onCreated={(strategy) => void handleStrategyCreated(strategy)}
        />
      ) : null}

      {reportContext ? (
        <WorkspaceGenerateReportDialog
          open={reportDialogOpen}
          onOpenChange={(open) => {
            if (!open) closeWizard();
          }}
          workspaceContext={reportContext}
          lockBrokerAccountCode={lockBrokerAccountCode}
          prefillStrategyId={wizard?.prefillStrategyId}
          prefillStrategyName={wizard?.prefillStrategyName}
          onGenerateStarted={handleReportGenerateStarted}
          onGenerateFailed={handleReportGenerateFailed}
          onReportGenerated={(result) => void handleReportGenerated(result)}
        />
      ) : null}

      {simulationContext && wizard ? (
        <WorkspaceSimulatorDialog
          open={simulationDialogOpen}
          onOpenChange={(open) => {
            if (!open) closeWizard();
          }}
          scope={scope}
          workspaceContext={simulationContext}
          parentNode={wizard.parentNode}
          scopedReportId={wizard.scopedReportId}
          lockBrokerAccountCode={
            wizard.policyRun?.lockBrokerAccountCode?.trim() || lockBrokerAccountCode
          }
          workspaceSimulationBlockedReason={workspaceSimulationBlockedReason}
          prefillStrategyId={wizard.prefillStrategyId}
          strategyLocked={Boolean(wizard.policyRun && wizard.prefillStrategyId)}
          prefillSymbol={wizard.policyRun?.prefillSymbol}
          prefillPolicyTree={wizard.policyRun?.prefillPolicyTree}
          prefillPolicy={wizard.policyRun?.prefillPolicy}
          policyMode={wizard.policyRun?.policyMode}
          prefillWindow={wizard.policyRun?.prefillWindow}
          defaultCandleTimeframe={wizard.policyRun?.candleTimeframe}
          candleTimeframeLocked={wizard.policyRun?.candleTimeframeLocked}
          entryCandleTimeframe={wizard.policyRun?.entryCandleTimeframe}
          managementCandleTimeframe={wizard.policyRun?.managementCandleTimeframe}
          title={wizard.policyRun ? "Edit & run simulation" : undefined}
          onSimulationQueued={handleSimulationQueued}
          onSimulationFailed={handleSimulationFailed}
          onSimulationSaved={(backtestId) => void handleSimulationSaved(backtestId)}
        />
      ) : null}

    </>
  );
}
